import classNames from "classnames";

import { IProps } from "./interaction-previews.types";
import { Button } from "@edgetier/components";
import { faEllipsisV } from "@fortawesome/pro-solid-svg-icons";
import "./interaction-previews.scss";

/**
 * Interaction previews that appear above or below the interaction modal. The previews can be clicked and it will
 * display the chosen interaction in the modal.
 * @param props.previewItems   The interactions that are being previewed.
 * @param props.onPreviewClick A function that will run when an interaction preview is clicked.
 * @param props.position       The position of the previews. Either before or after the current interaction.
 */
const InteractionPreviews = <T extends {}>({
    previewItems,
    onPreviewClick,
    position,
    loadMoreItems,
    children,
}: IProps<T>) => {
    /**
     * Create the appropriate class name based on the position of the preview item. A higher index means it's less
     * opaque. The index should be descending for previews before the modal and ascending for previews after it.
     * @param index The index of the interaction in the interactions array.
     * @returns A class name with an index indicating how opaque it is.
     */
    const getOpacityClass = (index: number) => {
        const opacityIndex = position === "before" ? previewItems.length - 1 - index : index;
        return `interaction-previews__item--${opacityIndex}`;
    };

    return (
        <div className="interaction-previews__container">
            <div
                className={classNames("interaction-previews", {
                    "interaction-previews--before": position === "before",
                    "interaction-previews--after": position === "after",
                })}
            >
                {previewItems.map((interaction, index) => (
                    <div
                        className={classNames("interaction-previews__item", getOpacityClass(index))}
                        onClick={() => onPreviewClick(interaction)}
                        key={index}
                    >
                        {children(interaction)}
                    </div>
                ))}
                {position === "after" && typeof loadMoreItems === "function" && previewItems.length <= 2 && (
                    <div className="interaction-previews__load-more">
                        <Button icon={faEllipsisV} onClick={loadMoreItems}>
                            Load More
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default InteractionPreviews;
